import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  AtSign,
  CheckCircle2,
  Eye,
  Fingerprint,
  KeyRound,
  LoaderCircle,
  LockKeyhole,
  Mail,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { FormEvent, useEffect, useRef, useState } from "react";

import {
  ApiError,
  CurrentSession,
  ExternalProvider,
  RegistrationPublicPolicy,
  api,
  authenticatePasskey,
  errorMessage,
} from "./api";
import { HumanChallenge } from "./HumanChallenge";
import { localeOptions, timeZoneOptions } from "./locale-options";
import { ThemeSwitcher } from "./theme";

type AuthView = "login" | "register" | "recover";

export function AuthScreen({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [view, setView] = useState<AuthView>(() =>
    window.location.pathname === "/register" ? "register" : "login");
  const reduceMotion = useReducedMotion();

  return (
    <main className="auth-shell">
      <section className="identity-pane" aria-labelledby="auth-title">
        <div className="auth-brand-row">
          <div className="identity-mark">
            <Eye aria-hidden="true" />
            <span>EvilEyeAuth</span>
          </div>
          <ThemeSwitcher />
        </div>

        <motion.div
          className="auth-content"
          key={view}
          initial={reduceMotion ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.24 }}
        >
          {view !== "login" && (
            <button className="text-button back-button" type="button" onClick={() => setView("login")}>
              <ArrowLeft aria-hidden="true" />
              返回登录
            </button>
          )}
          {view === "login" && <LoginForm onAuthenticated={onAuthenticated} onView={setView} />}
          {view === "register" && <RegistrationForm />}
          {view === "recover" && <RecoveryForm />}
        </motion.div>

        <footer className="auth-footer">
          <span>隐私</span>
          <span>条款</span>
          <span>安全</span>
        </footer>
      </section>

      <aside className="signal-pane" aria-hidden="true">
        <div className="signal-grid" />
        <div className="signal-status">
          <span className="status-dot" />
          IDENTITY NETWORK / ONLINE
        </div>
        <div className="trust-scope">
          <span className="scope-ring ring-one" />
          <span className="scope-ring ring-two" />
          <span className="scope-axis axis-x" />
          <span className="scope-axis axis-y" />
          <span className="scope-node node-identity"><Eye /></span>
          <span className="scope-node node-passkey"><Fingerprint /></span>
          <span className="scope-node node-policy"><ShieldCheck /></span>
          <span className="scope-node node-access"><KeyRound /></span>
          <span className="scope-sweep" />
        </div>
        <div className="signal-copy">
          <span>IDENTITY / ACCESS / TRUST</span>
          <strong>一个身份，连接可信应用。</strong>
          <p>账号、凭据与授权始终由你掌控。</p>
        </div>
      </aside>
    </main>
  );
}

function LoginForm({
  onAuthenticated,
  onView,
}: {
  onAuthenticated: () => void;
  onView: (view: AuthView) => void;
}) {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const providers = useQuery({
    queryKey: ["external-providers"],
    queryFn: () => api<{ providers: ExternalProvider[] }>("/api/v1/external-auth/providers"),
    retry: false,
  });
  const registrationPolicy = useQuery({
    queryKey: ["registration-policy"],
    queryFn: () => api<RegistrationPublicPolicy>("/api/v1/registrations/policy"),
    retry: false,
  });
  const login = useMutation({
    mutationFn: () =>
      api<CurrentSession>("/api/v1/session/login", {
        method: "POST",
        body: JSON.stringify({ identifier, password }),
      }),
    onSuccess: () => {
      setPassword("");
      onAuthenticated();
    },
  });
  const passkey = useMutation({
    mutationFn: authenticatePasskey,
    onSuccess: onAuthenticated,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    login.mutate();
  }

  return (
    <>
      <div className="heading-block">
        <span className="eyebrow">欢迎回来</span>
        <h1 id="auth-title">登录你的账号</h1>
        <p>继续前往账户与应用管理中心</p>
      </div>

      <form className="auth-form" onSubmit={submit}>
        <label>
          <span>用户名或邮箱</span>
          <span className="input-shell">
            <AtSign aria-hidden="true" />
            <input
              autoComplete="username"
              autoFocus
              maxLength={254}
              required
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
            />
          </span>
        </label>
        <label>
          <span>密码</span>
          <span className="input-shell">
            <LockKeyhole aria-hidden="true" />
            <input
              autoComplete="current-password"
              maxLength={1024}
              required
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </span>
        </label>
        {(login.error || passkey.error) && (
          <InlineError error={login.error ?? passkey.error} />
        )}
        <button className="primary-button wide" disabled={login.isPending} type="submit">
          {login.isPending ? <LoaderCircle className="spin" aria-hidden="true" /> : <KeyRound aria-hidden="true" />}
          使用密码登录
        </button>
      </form>

      <div className="auth-divider"><span>或</span></div>
      <button
        className="secondary-button wide"
        disabled={passkey.isPending}
        type="button"
        onClick={() => passkey.mutate()}
      >
        {passkey.isPending ? <LoaderCircle className="spin" aria-hidden="true" /> : <Fingerprint aria-hidden="true" />}
        使用 Passkey
      </button>

      {(providers.data?.providers.length ?? 0) > 0 && (
        <div className="provider-grid">
          {providers.data?.providers.map((provider) => (
            <button
              className="provider-button"
              key={provider.id}
              type="button"
              onClick={() => {
                window.location.assign(`/api/v1/external-auth/${provider.id}/start?returnTo=%2F`);
              }}
            >
              <span>{provider.displayName.slice(0, 1)}</span>
              {provider.displayName}
            </button>
          ))}
        </div>
      )}

      <div className="auth-actions">
        <button className="text-button" type="button" onClick={() => onView("recover")}>忘记密码</button>
        {registrationPolicy.data?.registrationEnabled !== false && <button className="text-button" type="button" onClick={() => onView("register")}>创建账号</button>}
        <a className="text-button" href="/appeal">账号申诉</a>
      </div>
    </>
  );
}

function RegistrationForm() {
  const policy = useQuery({
    queryKey: ["registration-policy"],
    queryFn: () => api<RegistrationPublicPolicy>("/api/v1/registrations/policy"),
    retry: false,
  });
  const [challenge, setChallenge] = useState<{ ready: boolean; token: string | null }>({ ready: false, token: null });
  const [challengeAttempt, setChallengeAttempt] = useState(0);
  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    displayName: "",
    locale: navigator.language || "en",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    termsAccepted: false,
    privacyAccepted: false,
    ageEligibilityConfirmed: false,
    invitationCode: "",
  });
  const lastSubmission = useRef<{ body: string; key: string } | null>(null);
  const registration = useMutation({
    mutationFn: ({ body, key }: { body: string; key: string }) =>
      api<{ status: string }>("/api/v1/registrations", {
        method: "POST",
        csrf: false,
        headers: { "Idempotency-Key": key },
        body,
      }),
    onError: () => setChallengeAttempt((value) => value + 1),
  });
  const resend = useMutation({
    mutationFn: () => api<{ status: string }>("/api/v1/registrations/email-verification/requests", {
      method: "POST",
      csrf: false,
      body: JSON.stringify({ email: form.email }),
    }),
  });

  if (policy.isPending) return <FlowLoading label="正在读取注册策略" />;
  if (policy.error) return <InlineError error={policy.error} />;
  if (!policy.data?.registrationEnabled) {
    return <ResultState icon={ShieldCheck} title="暂未开放注册" detail="当前平台未开放新账号注册。" />;
  }

  if (registration.isSuccess) {
    if (registration.data.status === "active") {
      return <ResultState icon={CheckCircle2} title="账号创建完成" detail="账号已经激活，现在可以返回登录。" />;
    }
    return (
      <><ResultState icon={Mail} title="检查你的邮箱" detail="如果注册信息有效，我们会发送验证邮件。完成验证后即可登录。" />
      <div className="auth-resend"><button className="secondary-button wide" disabled={resend.isPending || resend.isSuccess} type="button" onClick={() => resend.mutate()}><Mail />{resend.isSuccess ? "已重新发送" : "重新发送验证邮件"}</button>{resend.error && <InlineError error={resend.error} />}</div></>
    );
  }

  return (
    <>
      <div className="heading-block compact">
        <span className="eyebrow">新身份</span>
        <h1 id="auth-title">创建账号</h1>
        <p>建立你的 EvilEyeAuth 全局身份</p>
      </div>
      <form
        className="auth-form registration-form"
        onSubmit={(event) => {
          event.preventDefault();
          const canonicalBody = JSON.stringify({ ...form, marketingConsent: false });
          const body = JSON.stringify({ ...form, marketingConsent: false, challengeToken: challenge.token });
          const key = lastSubmission.current?.body === canonicalBody
            ? lastSubmission.current.key
            : `registration-${crypto.randomUUID()}`;
          lastSubmission.current = { body: canonicalBody, key };
          registration.mutate({ body, key });
        }}
      >
        <div className="field-pair">
          <label><span>用户名</span><input required minLength={3} maxLength={32} autoComplete="username" value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} /></label>
          <label><span>显示名称</span><input required maxLength={80} autoComplete="name" value={form.displayName} onChange={(event) => setForm({ ...form, displayName: event.target.value })} /></label>
        </div>
        <label><span>邮箱</span><input required maxLength={254} type="email" autoComplete="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
        {policy.data.invitationRequired && <label><span>邀请码</span><input required minLength={32} maxLength={32} autoComplete="off" value={form.invitationCode} onChange={(event) => setForm({ ...form, invitationCode: event.target.value.trim() })} /></label>}
        <label><span>密码</span><input required minLength={12} maxLength={1024} type="password" autoComplete="new-password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} /></label>
        <div className="field-pair">
          <label><span>语言</span><select required value={form.locale} onChange={(event) => setForm({ ...form, locale: event.target.value })}>{localeOptions(form.locale).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          <label><span>时区</span><select required value={form.timeZone} onChange={(event) => setForm({ ...form, timeZone: event.target.value })}>{timeZoneOptions(form.timeZone).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
        </div>
        <label className="check-row"><input type="checkbox" checked={form.termsAccepted} onChange={(event) => setForm({ ...form, termsAccepted: event.target.checked })} /><span>我同意服务条款</span></label>
        <label className="check-row"><input type="checkbox" checked={form.privacyAccepted} onChange={(event) => setForm({ ...form, privacyAccepted: event.target.checked })} /><span>我已阅读隐私声明</span></label>
        <label className="check-row"><input type="checkbox" checked={form.ageEligibilityConfirmed} onChange={(event) => setForm({ ...form, ageEligibilityConfirmed: event.target.checked })} /><span>我符合账号年龄要求</span></label>
        <HumanChallenge action="registration" onChange={setChallenge} resetKey={challengeAttempt} />
        {registration.error && <InlineError error={registration.error} />}
        <button className="primary-button wide" disabled={registration.isPending || !challenge.ready} type="submit">
          {registration.isPending ? <LoaderCircle className="spin" /> : <UserPlus />}
          创建账号
        </button>
      </form>
    </>
  );
}

export function EmailVerificationScreen() {
  const token = useOneTimeFragmentToken();
  const started = useRef(false);
  const verification = useMutation({
    mutationFn: (value: string) => api<void>("/api/v1/registrations/email-verification", {
      method: "POST",
      csrf: false,
      body: JSON.stringify({ token: value }),
    }),
    onSettled: clearOneTimeFragmentToken,
  });
  useEffect(() => {
    if (token && !started.current) {
      started.current = true;
      verification.mutate(token);
    }
  }, [token, verification]);

  return (
    <OneTimeFlowShell>
      {!token && <ResultState icon={Mail} title="验证链接无效" detail="链接没有包含可用的验证凭据，请重新发起注册。" />}
      {token && verification.isPending && <FlowLoading label="正在验证邮箱" />}
      {verification.error && <><InlineError error={verification.error} /><FlowReturn /></>}
      {verification.isSuccess && <><ResultState icon={CheckCircle2} title="邮箱验证完成" detail="账号已激活，现在可以使用用户名、邮箱或 Passkey 登录。" /><FlowReturn /></>}
    </OneTimeFlowShell>
  );
}

export function RecoveryCompletionScreen() {
  const token = useOneTimeFragmentToken();
  const started = useRef(false);
  const [grant, setGrant] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const verification = useMutation({
    mutationFn: (value: string) => api<{ grant: string; expiresAt: string }>("/api/v1/account-recovery/verifications", {
      method: "POST",
      csrf: false,
      body: JSON.stringify({ token: value }),
    }),
    onSuccess: (result) => setGrant(result.grant),
    onSettled: clearOneTimeFragmentToken,
  });
  const completion = useMutation({
    mutationFn: () => {
      if (!grant) throw new Error("恢复授权已失效，请重新请求恢复邮件。");
      if (newPassword !== confirmation) throw new Error("两次输入的新密码不一致。");
      return api<void>("/api/v1/account-recovery/completions", {
        method: "POST",
        csrf: false,
        body: JSON.stringify({ grant, newPassword }),
      });
    },
    onSuccess: () => {
      setGrant(null);
      setNewPassword("");
      setConfirmation("");
    },
  });
  useEffect(() => {
    if (token && !started.current) {
      started.current = true;
      verification.mutate(token);
    }
  }, [token, verification]);

  return (
    <OneTimeFlowShell>
      {!token && <><ResultState icon={Mail} title="恢复链接无效" detail="链接没有包含可用的恢复凭据，请重新请求恢复邮件。" /><FlowReturn /></>}
      {token && verification.isPending && <FlowLoading label="正在验证恢复链接" />}
      {verification.error && <><InlineError error={verification.error} /><FlowReturn /></>}
      {grant && !completion.isSuccess && (
        <>
          <div className="heading-block compact"><span className="eyebrow">账号恢复</span><h1>设置新密码</h1><p>完成后所有现有浏览器会话都会被撤销。</p></div>
          <form className="auth-form" onSubmit={(event) => { event.preventDefault(); completion.mutate(); }}>
            <label><span>新密码</span><input autoFocus required minLength={12} maxLength={1024} type="password" autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></label>
            <label><span>确认新密码</span><input required minLength={12} maxLength={1024} type="password" autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
            {completion.error && <InlineError error={completion.error} />}
            <button className="primary-button wide" disabled={completion.isPending} type="submit"><KeyRound />设置新密码</button>
          </form>
        </>
      )}
      {completion.isSuccess && <><ResultState icon={CheckCircle2} title="密码已更新" detail="旧会话已经撤销，请使用新密码登录。" /><FlowReturn /></>}
    </OneTimeFlowShell>
  );
}

function useOneTimeFragmentToken(): string | null {
  const [token] = useState(readOneTimeFragmentToken);
  return token;
}

let oneTimeFragmentToken: string | null | undefined;
let oneTimeFragmentPath: string | undefined;

function readOneTimeFragmentToken(): string | null {
  if (oneTimeFragmentPath === window.location.pathname && oneTimeFragmentToken !== undefined) {
    return oneTimeFragmentToken;
  }
  const value = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token");
  window.history.replaceState(null, "", window.location.pathname);
  oneTimeFragmentPath = window.location.pathname;
  oneTimeFragmentToken = value && /^[A-Za-z0-9_-]{43}$/.test(value) ? value : null;
  return oneTimeFragmentToken;
}

function clearOneTimeFragmentToken() {
  oneTimeFragmentToken = null;
}

function OneTimeFlowShell({ children }: { children: React.ReactNode }) {
  return <main className="verification-shell"><section className="verification-panel"><div className="auth-brand-row"><div className="identity-mark"><Eye /><span>EvilEyeAuth</span></div><ThemeSwitcher /></div><div className="one-time-flow">{children}</div></section></main>;
}

function FlowLoading({ label }: { label: string }) {
  return <div className="flow-loading" role="status"><LoaderCircle className="spin" /><span>{label}</span></div>;
}

function FlowReturn() {
  return <button className="secondary-button wide" type="button" onClick={() => window.location.assign("/")}><ArrowLeft />返回登录</button>;
}

function RecoveryForm() {
  const [email, setEmail] = useState("");
  const [challenge, setChallenge] = useState<{ ready: boolean; token: string | null }>({ ready: false, token: null });
  const [challengeAttempt, setChallengeAttempt] = useState(0);
  const recovery = useMutation({
    mutationFn: () =>
      api<{ status: string }>("/api/v1/account-recovery/requests", {
        method: "POST",
        csrf: false,
        body: JSON.stringify({ email, challengeToken: challenge.token }),
      }),
    onError: () => setChallengeAttempt((value) => value + 1),
  });

  if (recovery.isSuccess) {
    return (
      <ResultState
        icon={Mail}
        title="检查你的邮箱"
        detail="如果该账号可以恢复，我们会发送下一步操作邮件。"
      />
    );
  }

  return (
    <>
      <div className="heading-block compact">
        <span className="eyebrow">账号恢复</span>
        <h1 id="auth-title">重置密码</h1>
        <p>输入账号的主邮箱</p>
      </div>
      <form className="auth-form" onSubmit={(event) => { event.preventDefault(); recovery.mutate(); }}>
        <label><span>邮箱</span><span className="input-shell"><Mail /><input required maxLength={254} type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></span></label>
        <HumanChallenge action="recovery" onChange={setChallenge} resetKey={challengeAttempt} />
        {recovery.error && <InlineError error={recovery.error} />}
        <button className="primary-button wide" disabled={recovery.isPending || !challenge.ready} type="submit">
          {recovery.isPending ? <LoaderCircle className="spin" /> : <Mail />}
          发送恢复邮件
        </button>
      </form>
    </>
  );
}

export function SecondFactorGate({ session, onVerified }: { session: CurrentSession; onVerified: () => void }) {
  const [method, setMethod] = useState<"totp" | "recovery">("totp");
  const [value, setValue] = useState("");
  const verify = useMutation({
    mutationFn: () => api(`/api/v1/mfa/step-up/${method === "totp" ? "totp" : "recovery-code"}`, {
      method: "POST",
      body: JSON.stringify(method === "totp" ? { code: value } : { code: value }),
    }),
    onSuccess: () => { setValue(""); onVerified(); },
  });
  const passkey = useMutation({ mutationFn: authenticatePasskey, onSuccess: onVerified });

  return (
    <main className="verification-shell">
      <section className="verification-panel">
        <div className="auth-brand-row"><div className="identity-mark"><Eye /><span>EvilEyeAuth</span></div><ThemeSwitcher /></div>
        <div className="verification-icon"><ShieldCheck /></div>
        <div className="heading-block centered">
          <span className="eyebrow">安全验证</span>
          <h1>再确认一次是你</h1>
          <p>{session.displayName}，此账号已启用双重验证。</p>
        </div>
        <div className="segmented-control" aria-label="验证方式">
          <button className={method === "totp" ? "active" : ""} type="button" onClick={() => setMethod("totp")}>验证码</button>
          <button className={method === "recovery" ? "active" : ""} type="button" onClick={() => setMethod("recovery")}>恢复码</button>
        </div>
        <form className="auth-form" onSubmit={(event) => { event.preventDefault(); verify.mutate(); }}>
          <label><span>{method === "totp" ? "验证器代码" : "恢复码"}</span><input autoFocus required maxLength={64} inputMode={method === "totp" ? "numeric" : "text"} autoComplete="one-time-code" value={value} onChange={(event) => setValue(event.target.value)} /></label>
          {(verify.error || passkey.error) && <InlineError error={verify.error ?? passkey.error} />}
          <button className="primary-button wide" disabled={verify.isPending} type="submit">验证并继续</button>
        </form>
        <div className="auth-divider"><span>或</span></div>
        <button className="secondary-button wide" disabled={passkey.isPending} type="button" onClick={() => passkey.mutate()}><Fingerprint />使用 Passkey</button>
      </section>
    </main>
  );
}

export function useCurrentSession() {
  return useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      try {
        return await api<CurrentSession>("/api/v1/session");
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 15_000,
  });
}

export function useRefreshIdentity() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ["session"] });
    void client.invalidateQueries({ queryKey: ["account"] });
  };
}

function InlineError({ error }: { error: unknown }) {
  return <div className="inline-error" role="alert">{errorMessage(error)}</div>;
}

function ResultState({ icon: Icon, title, detail }: { icon: typeof Mail; title: string; detail: string }) {
  return (
    <div className="result-state" role="status">
      <span><Icon /></span>
      <h1>{title}</h1>
      <p>{detail}</p>
      <CheckCircle2 aria-hidden="true" />
    </div>
  );
}
